/**
 * Store for managing user session and authentication
 * @category Pinia
 * @module UserStore
 */

import { cloneDeep, intersection } from 'lodash-es'
import { $analytics } from '@/boot/analytics'
import { $flagsmith } from '@/boot/flagsmith'
import { $rollbar } from '@/boot/rollbar'
import { $pegApi } from '@/boot/peg-api'
import { isValidAddress } from '@/utils/validator.util'
import { removeUnusedFields } from '@/utils/form.util'
import { PERSONAL, BUSINESS } from '@/constants/account.const'

export const useUserStore = defineStore('user', () => {
  const $q = useQuasar()
  const { featureFlags } = useFeatureFlagStore()

  const user = ref({})
  const userWatchlist = reactive({ items: [], totalCount: 0 })
  const userActiveBids = ref([])
  const userIsUpdating = ref(false)

  const userDataExists = computed(() => Boolean(user.value?.id))
  const userIsBanned = computed(() => Boolean(user.value?.banned))
  const userHasAutoCheckout = computed(() => Boolean(user.value?.autoCheckout))
  const userHasFailedAutoCheckout = computed(() => Boolean(userHasAutoCheckout.value && user.value?.autoCheckoutDidFail))
  const userPaymentMethods = computed(() => user.value?.paymentMethods || [])
  const userDefaultPaymentMethod = computed(
    () => userPaymentMethods.value.find(pm => pm.token === user.value?.defaultPaymentToken) || null
  )
  // user has a valid default payment method if they have a default payment token
  // and the corresponding payment method exists and is not marked as failed or expired
  // (note: payment methods may be marked as deleted if removed from the payment gateway)
  const userHasValidDefaultPayment = computed(() =>
    Boolean(
      userDefaultPaymentMethod.value &&
      !userDefaultPaymentMethod.value.failed &&
      !userDefaultPaymentMethod.value.expired &&
      !userDefaultPaymentMethod.value.deleted
    )
  )
  // PEG-923 (epic PEG-823, AC5): bid-time requirements that are NOT part of
  // account completion (completion = the minimum identity set redefined in
  // PEG-921). A user can browse and complete their account without these, but
  // must satisfy them before bidding: a valid billing address and at least one
  // communication preference, on top of the existing auto-checkout + valid
  // default payment gates. These are derived (no persisted flag).
  // PEG-923 (PR #447 review): a billing address is satisfied by EITHER the
  // personal billing fields OR — for business accounts — any valid business
  // BILLING address (businesses[].addresses, addressType === 'BILLING'), which is
  // the address checkout/Braintree already consume. Validating only the personal
  // fields would wrongly block a business user whose billing lives on the
  // business profile from bidding.
  // PEG-823 (PR #450 review): only count the business BILLING address when the
  // account is actually a business account. A PERSONAL user can retain a stale
  // business record with a valid BILLING address (after switching account type,
  // or from migrated data); personal checkout consumes the personal billing
  // fields, NOT that business address, so honoring it here would let userCanBid
  // pass without the address the personal flow actually uses.
  const userHasBillingAddress = computed(
    () =>
      isValidAddress({
        address1: user.value?.billingAddress1,
        city: user.value?.billingCity,
        state: user.value?.billingState,
        zip: user.value?.billingZip,
      }) ||
      (userHasBusinessAccount.value && userBusinessBillingAddresses.value.some(addr => isValidAddress(addr)))
  )
  // PEG-923 (PR #447 review): the transactional SMS/email opt-in controls only
  // render when `account_sms_communication` is enabled (and that flag is absent
  // from defaultFlags, so it is off by default). When the controls are hidden a
  // user has no UI to set smsOptIn/emailOptIn, so requiring one would block them
  // indefinitely — treat the requirement as satisfied wherever it is not
  // surfaceable.
  const userHasCommunicationPrefs = computed(
    () => !featureFlags.accountSmsCommunication?.enabled || Boolean(user.value?.smsOptIn || user.value?.emailOptIn)
  )

  const userCanBid = computed(() =>
    Boolean(
      userDataExists.value &&
      userHasAutoCheckout.value &&
      userHasValidDefaultPayment.value &&
      userHasBillingAddress.value &&
      userHasCommunicationPrefs.value &&
      !userIsBanned.value &&
      !userHasFailedAutoCheckout.value
    )
  )

  // PEG-923: the specific bid-time requirements a (non-banned) user still needs
  // to satisfy, in the order the bid-restriction surface should route them. Used
  // by the bid-restriction alert (ProductCannotBidAlert) to explain what is
  // missing; empty when the user can bid (or is banned / has a failed
  // auto-checkout, which are handled by their own dedicated alerts).
  const userMissingBidRequirements = computed(() => {
    if (!userDataExists.value || userIsBanned.value || userHasFailedAutoCheckout.value) return []
    // Each entry carries the route the bid-restriction surface should send the
    // user to in order to actually satisfy that requirement (PR #447 review):
    // billing + communication prefs live on the account profile/setup pages, NOT
    // on AccountWallet, which only hosts the payment + auto-checkout surfaces.
    // Business billing is edited on the business profile page.
    const missing = []
    if (!userHasBillingAddress.value) {
      missing.push({
        label: 'billing address',
        route: { name: userHasBusinessAccount.value ? 'AccountBusiness' : 'AccountHome' },
      })
    }
    if (!userHasCommunicationPrefs.value) {
      missing.push({ label: 'communication preferences', route: { name: 'AccountHome' } })
    }
    if (!userHasValidDefaultPayment.value) {
      missing.push({ label: 'payment method', route: { name: 'AccountWallet' } })
    }
    if (!userHasAutoCheckout.value) {
      missing.push({ label: 'auto-checkout', route: { name: 'AccountWallet' } })
    }
    return missing
  })
  const userIsInternal = computed(() => Boolean(user.value?.isInternal))
  const userHasVerifiedPhone = computed(() => Boolean(user.value?.phoneVerified))
  const userHasVerifiedEmail = computed(() => Boolean(user.value?.emailVerified))

  const userBaseProfile = computed(() => {
    return {
      // email: user.value?.email || null,
      firstName: user.value?.firstName || null,
      lastName: user.value?.lastName || null,
      phone: user.value?.phone || null,
      email: user.value?.email || null,
    }
  })
  const userEmailSmsConsent = computed(() => {
    return {
      emailOptIn: user.value?.emailOptIn,
      emailOptInMarketing: user.value?.emailOptInMarketing,
      smsOptIn: user.value?.smsOptIn,
      smsOptInMarketing: user.value?.smsOptInMarketing,
    }
  })
  const userBillingAddress = computed(() => {
    return {
      address1: user.value?.billingAddress1 || null,
      address2: user.value?.billingAddress2 || null,
      city: user.value?.billingCity || null,
      state: user.value?.billingState || null,
      zip: user.value?.billingZip || null,
    }
  })
  // TODO: allow users to store multiple shipping addresses similar to businesses
  const userShippingAddress = computed(() => {
    return {
      address1: user.value?.shippingAddress1 || null,
      address2: user.value?.shippingAddress2 || null,
      city: user.value?.shippingCity || null,
      state: user.value?.shippingState || null,
      zip: user.value?.shippingZip || null,
    }
  })
  const accountEmailVerified = computed(() => {
    return Boolean(user.value?.emailVerified)
  })
  const userHasPersonalAccount = computed(() => {
    return Boolean(user.value.signUpType === PERSONAL)
  })
  const userHasBusinessAccount = computed(() => {
    return Boolean(user.value.signUpType === BUSINESS)
  })

  const userAddressStates = computed(() => {
    return [
      user.value?.billingState,
      user.value?.shippingState,
      user.value?.businesses?.reduce((acc, b) => {
        return acc.concat(b.addresses.map(addr => addr.state))
      }, []),
    ]
      .flat()
      .filter(Boolean)
  })

  const userIsSmsMarketingExcluded = computed(() => {
    return featureFlags.marketingSmsExcludedStates?.value?.some(state => userAddressStates.value.includes(state))
  })

  /* ============ BUSINESS PROFILE ============ */
  const userBusiness = computed(() => {
    return (user.value?.businesses || []).find(b => b.id === user.value.currentBusinessId) || user.value?.businesses?.[0] || {}
  })
  const userBusinessRoles = computed(() => {
    let roles = userBusiness.value?.userRoles || []
    return roles.map(r => r.name)
  })
  const userIsBusinessAdmin = computed(() => {
    return Boolean(intersection(userBusinessRoles.value, ['owner', 'admin']).length)
  })
  const userBusinessComplianceInfo = computed(() => {
    return {
      taxId: userBusiness.value?.taxId || '',
      resellerFlag: userBusiness.value?.resellerFlag || false,
      resaleCertificateNumber: userBusiness.value?.resaleCertificateNumber || '',
      resaleCertificateUrl: userBusiness.value?.resaleCertificateUrl || '',
    }
  })
  const userBusinessOfficeAddress = computed(() => {
    return userBusiness.value?.addresses?.find(addr => addr.addressType === 'OFFICE') || {}
  })
  const userBusinessShippingAddresses = computed(() => {
    return userBusiness.value?.addresses?.filter(addr => addr.addressType === 'SHIPPING') || []
  })
  const userBusinessBillingAddresses = computed(() => {
    return userBusiness.value?.addresses?.filter(addr => addr.addressType === 'BILLING') || []
  })

  // identify user to third-party services when user data changes
  watch(
    userDataExists,
    (curr, prev) => {
      if (curr && !prev) {
        identifyUserToThirdParties()
      }
    },
    { immediate: true }
  )

  function setUpdating(value) {
    userIsUpdating.value = value
  }

  /**
   * Get user data
   * @memberOf module:UserStore
   * @returns {Promise} - Promise with user data
   */
  async function getUser() {
    const resp = await $pegApi.Users.get()
    user.value = resp?.user || {}

    return resp
  }

  /**
   * Best-effort user refresh after a successful mutation.
   * @memberOf module:UserStore
   * @description The mutation has already succeeded, so a transient refresh
   * failure (getUser() now throws on non-404 API/network errors) must NOT reject
   * the operation — callers like PegForm would report an already-saved change as
   * failed and prompt a resubmit. Logs and continues.
   * @returns {Promise<void>}
   */
  async function refreshUser() {
    try {
      await getUser()
    } catch (error) {
      $rollbar.error('Failed to refresh user after a successful mutation', error)
    }
  }

  /**
   * Get the user's watchlist items and store them on userWatchlist.
   * @memberOf module:UserStore
   * @returns {Promise<void>} - Resolves once the store state is updated
   */
  async function getUserWatchlist() {
    try {
      const { watchlistItems = [], meta = {} } = await $pegApi.Users.WatchlistItems.getAll()
      userWatchlist.items = watchlistItems
      userWatchlist.totalCount = meta.totalCount || 0
    } catch (error) {
      // $pegApi now throws on error; callers (session init, AccountWatchlist
      // mount) fire this without awaiting, so swallow + log to avoid an
      // unhandled rejection. Mirrors getUserActiveBids().
      $rollbar.error('Failed to fetch user watchlist', error)
      userWatchlist.items = []
      userWatchlist.totalCount = 0
    }
  }

  /**
   * Get the user's active bids (auctions where user is currently winning)
   * @memberOf module:UserStore
   * @returns {void}
   */
  function getUserActiveBids() {
    $pegApi.AuctionItems.getBulk({ status: 'AUCTION,UNPAID,WON', userIsWinning: true })
      .then(({ auctionItems = [] }) => {
        const activeAuctions = auctionItems.map(item => {
          const { id, title, images, currentBid } = item
          return { id, title, images, currentBid }
        })
        userActiveBids.value = activeAuctions

        // $flagsmith.setTrait('hasActiveAuctions', activeAuctions.length > 0)
      })
      .catch(() => {
        userActiveBids.value = []
      })
  }

  /**
   * Initialize user session
   * @memberOf module:UserStore
   * @async
   * @returns {Promise} - Promise with success status
   */
  async function init() {
    // getUser() resolves only on success (it throws on non-404 errors and
    // returns a null-user sentinel on 404), so there's no success flag to read
    // here — a failure propagates to the caller (sessionStore.init wraps this).
    await getUser()

    if (userDataExists.value) {
      // opt-out from sms marketing if user is in a restricted state and has opted in
      if (userIsSmsMarketingExcluded.value && user.value.smsOptInMarketing) {
        // fire-and-forget background sync; updateUser now throws on API error,
        // so swallow + log to avoid an unhandled rejection during session init.
        updateUser({ smsOptInMarketing: false }).catch(error => {
          $rollbar.error('Failed to opt out of SMS marketing for excluded market', error)
        })
      }

      // fetch user's watchlist
      getUserWatchlist()
      // fetch count of user's active auctions
      getUserActiveBids()

      // fetch customer data from payment gateway to ensure payment methods are up-to-date in our system
      // MUST AWAIT to prevent race conditions with payment form also initializing
      // MUST USE DYNAMIC STORE RESOLUTION to avoid circular dependency
      await usePaymentGatewayStore().gatewayInit({ customerOnly: true })
    }

    return { success: true }
  }

  /**
   * Sign user out, clear user data and auth token
   * @memberOf module:UserStore
   * @returns {void}
   */
  async function resetState() {
    user.value = {}
    userWatchlist.items = []
    userWatchlist.totalCount = 0
    userActiveBids.value = []
    userIsUpdating.value = false
  }

  /**
   * Identify the user to third-party services like analytics and feature flags
   * @memberOf module:SessionStore
   */
  function identifyUserToThirdParties() {
    if (user.value) {
      // identify user in analytics
      $analytics.identify(user.value)

      $flagsmith.identify(user.value.id, {
        hasBusinessAccount: userHasBusinessAccount.value,
        hasAutoCheckout: userHasAutoCheckout.value,
        autoCheckoutFailed: userHasFailedAutoCheckout.value,
        isBanned: userIsBanned.value,
      })

      // rollbar - track user session
      $rollbar.configure({
        payload: {
          person: {
            id: user.value.id,
          },
        },
      })
    }
  }

  /**
   * Update user data in PEG API
   * @memberOf module:UserStore
   * @param {Object} data - User data to update
   * @returns {Promise} - Promise with success status
   */
  async function updateUser(data) {
    setUpdating(true)

    try {
      const { user: updatedUser } = await $pegApi.Users.update(data)

      if (updatedUser) {
        Object.assign(user.value, updatedUser)
      } else {
        await refreshUser() // best-effort re-fetch; the update already succeeded
      }

      return { success: true }
    } finally {
      // Always clear the updating flag — $pegApi.Users.update() now throws on
      // error, so this must run on the rejection path too (the throw propagates
      // to the caller / PegForm's catch).
      setUpdating(false)
    }
  }

  /**
   * Save user address (shipping or billing)
   * @memberOf module:UserStore
   * @param {Object} data - Address data
   * @param {String} type - Address type ('shipping' or 'billing')
   * @returns {Promise} - Promise with success status
   */
  async function saveUserAddress(data, type) {
    // map address fields with appropriate prefix
    data[`${type}Address1`] = data.address1 || null
    data[`${type}Address2`] = data.address2 || null
    data[`${type}Company`] = data.company || null
    data[`${type}City`] = data.city || null
    data[`${type}State`] = data.state || null
    data[`${type}Zip`] = data.zip || null

    // remove generic address fields
    const payload = removeUnusedFields(data, ['firstName', 'lastName', 'address1', 'address2', 'company', 'city', 'state', 'zip'])

    return updateUser(payload)
  }

  function saveUserBillingAddress(data) {
    return saveUserAddress(data, 'billing')
  }

  async function saveUserShippingAddress(data) {
    // Use shipping as a billing default if none is set. Best-effort — updateUser()
    // now throws, and this is an optional convenience copy, so a failure here must
    // not block the required shipping save below (the form is wired to this action).
    if (!isValidAddress(userBillingAddress.value)) {
      try {
        await saveUserBillingAddress(cloneDeep(data))
      } catch (error) {
        $rollbar.error('Failed to default billing address from shipping (best-effort)', error)
      }
    }

    return saveUserAddress(data, 'shipping')
  }

  /**
   * Save or update the user's business profile
   * @memberOf module:UserStore
   * @param {Object} data - Business profile data
   * @returns {Promise} - Promise with request status
   */
  async function saveBusinessProfile(data) {
    setUpdating(true)

    const payload = removeUnusedFields(data, [
      'addresses',
      'dateCreated',
      'dateModified',
      'id',
      'resaleCertificateUrl',
      'userRoles',
    ])

    try {
      const resp = userBusiness.value?.id
        ? // update existing business profile
          await $pegApi.Businesses.update(userBusiness.value.id, payload)
        : await $pegApi.Businesses.create(payload)

      // re-fetch user data to ensure business info is up-to-date (best-effort;
      // the create/update already succeeded)
      await refreshUser()

      return resp
    } catch (error) {
      // $pegApi.Businesses.create/update now throw on error. Preserve the
      // friendly duplicate-TIN message mapping the form relies on, then
      // re-throw so PegForm's catch surfaces it via onError(error).
      if (error?.message && error.message.toLowerCase().includes('tax id already exists')) {
        error.message =
          'A business with this tax ID already exists. Please double-check your records and try again, or contact support for assistance.'
      }
      throw error
    } finally {
      setUpdating(false)
    }
  }

  async function saveBusinessAddress(data) {
    setUpdating(true)

    try {
      const resp = data.id
        ? await $pegApi.Businesses.updateAddress(userBusiness.value.id, data.id, data)
        : await $pegApi.Businesses.addAddress(userBusiness.value.id, data)

      // best-effort refresh; the address create/update already succeeded
      await refreshUser()

      return resp
    } finally {
      // $pegApi.Businesses.addAddress/updateAddress now throw on error; clear
      // the updating flag on the rejection path too (the throw propagates to
      // PegForm's catch).
      setUpdating(false)
    }
  }

  async function saveBusinessBillingAddress(data) {
    // save billing address
    return saveBusinessAddress({ ...data, addressType: 'BILLING' })
  }

  async function saveBusinessShippingAddress(data) {
    // save shipping address
    return saveBusinessAddress({ ...data, addressType: 'SHIPPING' })
  }

  async function saveBusinessOfficeAddress(data) {
    // save office address
    const resp = await saveBusinessAddress({ ...data, addressType: 'OFFICE' })

    const hasBillingAddress = userBusiness.value?.addresses?.some(addr => addr.addressType === 'BILLING')
    const hasShippingAddress = userBusiness.value?.addresses?.some(addr => addr.addressType === 'SHIPPING')

    // use office address for shipping/billing if not already set
    if (!hasBillingAddress || !hasShippingAddress) {
      // clean up details given this is done by default
      // users can provide these details later if desired
      delete data.id

      // use account first/last name if not set
      if (!data.firstName) {
        data.firstName = user.value.firstName || null
      }
      if (!data.lastName) {
        data.lastName = user.value.lastName || null
      }

      // Best-effort auto-creation of shipping/billing copies — the office save
      // above is authoritative, and these addAddress calls now throw, so a
      // failed copy must not reject the (already-saved) office PegForm result.
      if (!hasShippingAddress) {
        try {
          await saveBusinessShippingAddress(cloneDeep(data))
        } catch (error) {
          $rollbar.error('Failed to auto-create business shipping address from office (best-effort)', error)
        }
      }

      if (!hasBillingAddress) {
        try {
          await saveBusinessBillingAddress(cloneDeep(data))
        } catch (error) {
          $rollbar.error('Failed to auto-create business billing address from office (best-effort)', error)
        }
      }
    }

    return resp
  }

  /**
   * Set the user's default payment method
   * @memberOf module:UserStore
   * @param {String} token - Payment token
   * @returns {Promise} - Promise with request status
   */
  async function saveUserDefaultPayment(token) {
    return await updateUser({ paymentSaved: true, defaultPaymentToken: token })
  }

  /**
   * Add an auction item to the user's watchlist
   * @memberOf module:UserStore
   * @param {Number} auctionItemId - Auction item ID
   * @param {Boolean} options.notify - Show notification on success/failure
   * @returns {Promise} - Promise with request status
   */
  async function addWatchlistItem(auctionItemId, options = {}) {
    const { notify = true } = options

    // check if item is already in watchlist
    if (userWatchlist.items.some(item => item.auctionItemId === auctionItemId)) {
      return { success: true }
    }
    setUpdating(true)

    try {
      const resp = await $pegApi.Users.WatchlistItems.add(auctionItemId)

      // re-fetch watchlist
      getUserWatchlist()

      if (notify) {
        $analytics.interaction('Added Watchlist Item', { item_id: auctionItemId })
        $q.notify({ type: 'success', message: 'Item added to watchlist' })
      }

      return { success: true, ...resp }
    } catch (error) {
      // PEG-826: this store action fully owns the failure UX (Rollbar + toast)
      // and its only callers are fire-and-forget click handlers (ProductBidForm,
      // ProductHeader) that neither await nor catch. Returning a { success:false }
      // result instead of rethrowing keeps that contract and avoids the throwing
      // API turning every failed click into an unhandled promise rejection.
      $rollbar.error('Failed to add watchlist item', error)
      if (notify) {
        $q.notify({ type: 'error', message: 'Failed to add item to watchlist' })
      }
      return { success: false, message: error?.message }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Remove an auction item from the user's watchlist
   * @memberOf module:UserStore
   * @param {Object} item - Watchlist item
   * @param {Boolean} options.notify - Show notification on success/failure
   * @returns {Promise} - Promise with request status
   */
  async function removeWatchlistItem(item, options = {}) {
    const { notify = true } = options
    const { id, watchlistItemId } = item

    try {
      const resp = await $pegApi.Users.WatchlistItems.remove(watchlistItemId)

      userWatchlist.items = userWatchlist.items.filter(item => item.id !== watchlistItemId)
      userWatchlist.totalCount--

      if (notify) {
        $analytics.interaction('Removed Watchlist Item', { item_id: id })
        $q.notify({ type: 'success', message: 'Item removed from watchlist' })
      }

      return { success: true, ...resp }
    } catch (error) {
      // PEG-826: see addWatchlistItem — fire-and-forget click callers
      // (AccountWatchlistItem, ProductHeader) do not await/catch, so handle the
      // failure here and return { success:false } rather than rethrowing into an
      // unhandled rejection under the throwing API model.
      $rollbar.error('Failed to remove watchlist item', error)
      if (notify) {
        $q.notify({ type: 'error', message: 'Failed to remove item from watchlist' })
      }
      return { success: false, message: error?.message }
    }
  }

  /**
   * Update a user's payment method
   * @memberOf module:UserStore
   * @param {String} id - Payment method ID
   * @param {Object} data - Payment method data to update
   * @returns {Promise} - Promise with request status
   */
  async function updateUserPaymentMethod(id, data) {
    setUpdating(true)

    try {
      const resp = await $pegApi.Users.PaymentMethods.update(id, data)

      // re-fetch user data to ensure payment methods are up-to-date (best-effort;
      // the update already succeeded)
      await refreshUser()

      return resp
    } catch (error) {
      $rollbar.error('Failed to update payment method:', error)
      throw error
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Remove a user's payment method (mark as deleted in PEG)
   * @memberOf module:UserStore
   * @param {String} methodId - Payment method ID
   * @returns {Promise} - Promise with request status
   */
  async function removeUserPaymentMethod(methodId) {
    const resp = await $pegApi.Users.PaymentMethods.remove(methodId)
    // re-fetch user payment methods (best-effort; the delete already succeeded)
    await refreshUser()

    return resp
  }

  /**
   * Sync payment methods stored in the payment gateway with the Peg user profile
   * @memberOf module:UserStore
   * @param {Array} gatewayPaymentMethods - List of payment methods from the payment gateway
   * @returns {Promise<void>} - Resolves once the sync ops settle and the user refresh completes
   */
  async function syncGatewayPaymentMethods(gatewayPaymentMethods) {
    const proms = []

    // add/update payment methods to user profile
    gatewayPaymentMethods.forEach(({ token, expired = false }) => {
      const existing = userPaymentMethods.value.find(pm => pm.token === token)
      if (!existing) {
        // add new payment method
        proms.push($pegApi.Users.PaymentMethods.add({ token, expired }))
      } else if (existing.expired !== expired) {
        // sync expired flag
        proms.push($pegApi.Users.PaymentMethods.update(existing.id, { expired }))
      }
    })
    // mark payment methods that no longer exist in gateway as deleted
    userPaymentMethods.value
      .filter(m => !m.deleted)
      .forEach(({ token, id }) => {
        if (!gatewayPaymentMethods.some(pm => pm.token === token)) {
          proms.push($pegApi.Users.PaymentMethods.remove(id))
        }
      })

    // re-fetch user if any payment methods changed
    // PEG-826: $pegApi.Users.PaymentMethods.add/update/remove now reject on
    // error. Promise.all would short-circuit on the first rejection, leave an
    // unhandled rejection, and skip the post-sync getUser() refresh even when
    // other sync ops succeeded. Use allSettled so partial syncs still refresh,
    // and log any rejected ops to Rollbar.
    if (!proms.length) return

    const results = await Promise.allSettled(proms)
    const failures = results.filter(r => r.status === 'rejected')
    if (failures.length) {
      $rollbar.error('Failed to sync gateway payment methods', {
        failed: failures.length,
        total: results.length,
        reasons: failures.map(r => r.reason?.message),
      })
    }
    // Best-effort post-sync refresh; getUser() now rejects on non-404 errors, so
    // refreshUser() swallows + logs to avoid an unhandled rejection.
    await refreshUser()
  }

  /**
   * Submit account deletion request, then re-fetch user so status reflects PENDING_DELETION
   * @param {Object} data - { platform, reason? }
   * @returns {Promise} - API response
   */
  async function requestAccountDeletion(data) {
    const resp = await $pegApi.AccountDeletion.createRequest(data)
    // best-effort refresh so status reflects PENDING_DELETION; the deletion
    // request was already created, so a failed refresh must not reject it
    await refreshUser()
    return resp
  }

  return {
    // state
    user,
    userWatchlist,
    userActiveBids,
    userIsUpdating,
    // computed
    userBaseProfile,
    userBillingAddress,
    userShippingAddress,
    accountEmailVerified,
    userBusiness,
    userBusinessRoles,
    userBusinessComplianceInfo,
    userBusinessOfficeAddress,
    userBusinessShippingAddresses,
    userBusinessBillingAddresses,
    userHasBusinessAccount,
    userHasPersonalAccount,
    userEmailSmsConsent,
    userHasVerifiedPhone,
    userHasVerifiedEmail,
    userHasAutoCheckout,
    userHasFailedAutoCheckout,
    userHasValidDefaultPayment,
    userHasBillingAddress,
    userHasCommunicationPrefs,
    userMissingBidRequirements,
    userIsBanned,
    userIsInternal,
    userIsBusinessAdmin,
    userIsSmsMarketingExcluded,
    userCanBid,
    userDataExists,
    userPaymentMethods,
    userDefaultPaymentMethod,
    // actions
    init,
    resetState,
    saveBusinessAddress,
    saveBusinessOfficeAddress,
    saveBusinessShippingAddress,
    saveBusinessBillingAddress,
    saveBusinessProfile,
    addWatchlistItem,
    getUserWatchlist,
    removeWatchlistItem,
    saveUserDefaultPayment,
    saveUserBillingAddress,
    saveUserShippingAddress,
    syncGatewayPaymentMethods,
    removeUserPaymentMethod,
    updateUserPaymentMethod,
    getUser,
    refreshUser,
    updateUser,
    requestAccountDeletion,
  }
})
